import React, {useState, useMemo} from 'react';
import {
  Autocomplete,
  Box,
  Button, 
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WaterIcon from '@mui/icons-material/Water';
import BoltIcon from '@mui/icons-material/Bolt';
import GasMeterIcon from '@mui/icons-material/GasMeter';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import {
  Meter,
  useGetPropertiesQuery,
  useGetUsersQuery, usePatchPropertyMutation,
  useDeletePropertyByIdMutation,
  usePostPropertyMutation,
  User
} from '../../redux/residentPortalApi';
import {developmentInfo, formatPropertyType, formatPropertyNumber} from "../../utils/propertyUtils";
import {SelectChangeEvent} from "@mui/material/Select";

type MeterConnectionType = 'wifi' | 'token';

const TOKEN_METER_DEFAULTS = {
  concentratorId: '202509013',
  meterType: 'token',
  provider: 'token',
  protocolId: 30000,
  remoteTopUpSupported: true,
} as const;

const getMeterConnectionType = (meter?: Meter): MeterConnectionType => {
  if (meter?.meterType === 'token' || meter?.provider === 'token') {
    return 'token';
  }

  return 'wifi';
};

const sanitizeUnitNumber = (value: string): string => value.replace(/\s+/g, '').toUpperCase();

interface PropertyState {
  development: string;
  type: string;
  unitNumber: string;
  phase: string;
  ownerIds: string[];
  waterMeterImei?: string;
  waterMeterConnectionType: MeterConnectionType;
  electricityMeterImei?: string;
  electricityMeterConnectionType: MeterConnectionType;
  gasMeterImei?: string;
  gasMeterConnectionType: MeterConnectionType;
}

const initialPropertyState: PropertyState = {
  development: '',
  type: '',
  unitNumber: '',
  phase: '',
  ownerIds: [],
  waterMeterImei: '',
  waterMeterConnectionType: 'wifi',
  electricityMeterImei: '',
  electricityMeterConnectionType: 'wifi',
  gasMeterImei: '',
  gasMeterConnectionType: 'wifi'
};

// Mode for the property modal (add or edit)
type ModalMode = 'add' | 'edit';

// Type for sorting
type Order = 'asc' | 'desc';
type OrderBy = 'development' | 'type' | 'unitNumber';

const Properties = () => {
  const [open, setOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('add');
  const [editPropertyId, setEditPropertyId] = useState<string | null>(null);
  const [newProperty, setNewProperty] = useState<PropertyState>(initialPropertyState);
  const [visibleMeters, setVisibleMeters] = useState<{water: boolean, electricity: boolean, gas: boolean}>({
    water: false,
    electricity: false,
    gas: false
  });
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterDevelopment, setFilterDevelopment] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterOwnerEmail, setFilterOwnerEmail] = useState<string>('');
  const [filterNoEmail, setFilterNoEmail] = useState<boolean>(false);
  
  // Sorting states
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<OrderBy>('development');
  
  // Pagination states
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {data: properties, isLoading, isError} = useGetPropertiesQuery();
  const [postProperty] = usePostPropertyMutation();
  const [patchProperty] = usePatchPropertyMutation();
  const [deletePropertyById] = useDeletePropertyByIdMutation();
  const {data: users} = useGetUsersQuery();
  const ownerEmailById = useMemo(() => {
    return new Map((users ?? []).map((user) => [user.id ?? '', user.email ?? '']));
  }, [users]);
  
  // Function to handle sorting
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  // Function to handle pagination
  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };
  
  // Function to handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page when searching
  };
  
  // Function to handle development filter
  const handleDevelopmentFilterChange = (event: SelectChangeEvent) => {
    setFilterDevelopment(event.target.value);
    setPage(1); // Reset to first page when filtering
  };
  
  // Function to handle type filter
  const handleTypeFilterChange = (event: SelectChangeEvent) => {
    setFilterType(event.target.value);
    setPage(1); // Reset to first page when filtering
  };

  // Function to handle owner email filter
  const handleOwnerEmailFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterOwnerEmail(event.target.value);
    setPage(1);
  };

  // Function to handle no-email filter
  const handleFilterNoEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterNoEmail(event.target.checked);
    setPage(1);
  };

  const handleDeleteProperty = async (propertyId: string, propertyLabel: string) => {
    const confirmed = window.confirm(`Delete ${propertyLabel}? This will permanently remove the property.`);

    if (!confirmed) {
      return;
    }

    try {
      await deletePropertyById({ id: propertyId }).unwrap();
    } catch (error) {
      console.error('Failed to delete property:', error);
      window.alert('Failed to delete the property. Please try again.');
    }
  };

  const handleExportFilteredProperties = () => {
    if (!filteredAndSortedProperties.length) {
      window.alert('No properties found for the selected filters.');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(
      filteredAndSortedProperties.map((property) => ({
        Development: developmentInfo(property.development).name,
        'Unit Number': formatPropertyNumber(property),
        Type: property.type ? formatPropertyType(property.type) : '',
        'Owner Email': ownerEmailById.get(property.ownerIds?.[0] ?? '') ?? '',
      })),
    );

    worksheet['!cols'] = [
      { wch: 24 },
      { wch: 16 },
      { wch: 18 },
      { wch: 32 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Properties');
    XLSX.writeFile(workbook, `properties-export-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  
  // Filter and sort properties
  const filteredAndSortedProperties = useMemo(() => {
    if (!properties) return [];
    
    // First, filter the properties
    let filtered = properties.filter(property => {
      // Filter by search term (check development name, unit number, and type)
      const searchMatch = searchTerm === '' || 
        developmentInfo(property.development).name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        formatPropertyNumber(property).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (property.type && formatPropertyType(property.type).toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Filter by development
      const developmentMatch = filterDevelopment === '' || property.development === filterDevelopment;
      
      // Filter by type
      const typeMatch = filterType === '' || property.type === filterType;

      // Filter by owner email
      const ownerEmailMatch = filterOwnerEmail === '' ||
        (property.ownerEmails ?? []).some(email =>
          email.toLowerCase().includes(filterOwnerEmail.toLowerCase())
        );

      // Filter properties with no owner email
      const noEmailMatch = !filterNoEmail || (property.ownerEmails ?? []).length === 0;

      return searchMatch && developmentMatch && typeMatch && ownerEmailMatch && noEmailMatch;
    });
    
    // Then, sort the filtered properties
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (orderBy) {
        case 'development':
          comparison = developmentInfo(a.development).name.localeCompare(developmentInfo(b.development).name);
          break;
        case 'type':
          const typeA = a.type ? formatPropertyType(a.type) : '';
          const typeB = b.type ? formatPropertyType(b.type) : '';
          comparison = typeA.localeCompare(typeB);
          break;
        case 'unitNumber':
          comparison = formatPropertyNumber(a).localeCompare(formatPropertyNumber(b));
          break;
        default:
          break;
      }
      
      return order === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [properties, searchTerm, filterDevelopment, filterType, filterOwnerEmail, filterNoEmail, order, orderBy]);
  
  // Paginate properties
  const paginatedProperties = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredAndSortedProperties.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedProperties, page, rowsPerPage]);

  const handleOpen = () => {
    setModalMode('add');
    setEditPropertyId(null);
    setNewProperty(initialPropertyState);
    setOpen(true);
  };
  
  const handleOpenEdit = (property: any) => {
    setModalMode('edit');
    setEditPropertyId(property.id);
    
    // Initialize visible meters based on existing property meters
    const propertyMeters = property.meters || [];
    const hasWaterMeter = propertyMeters.some((meter: Meter) => meter.service === 'water');
    const hasElectricityMeter = propertyMeters.some((meter: Meter) => meter.service === 'electricity');
    const hasGasMeter = propertyMeters.some((meter: Meter) => meter.service === 'gas');
    
    setVisibleMeters({
      water: hasWaterMeter,
      electricity: hasElectricityMeter,
      gas: hasGasMeter
    });
    
    // Set property data for editing
    const waterMeter = propertyMeters.find((meter: Meter) => meter.service === 'water');
    const electricityMeter = propertyMeters.find((meter: Meter) => meter.service === 'electricity');
    const gasMeter = propertyMeters.find((meter: Meter) => meter.service === 'gas');
    
    setNewProperty({
      development: property.development,
      type: property.type,
      unitNumber: property.unitNumber.toString(),
      phase: property.phase,
      ownerIds: property.ownerIds || [],
      waterMeterImei: waterMeter ? waterMeter.imei : '',
      waterMeterConnectionType: getMeterConnectionType(waterMeter),
      electricityMeterImei: electricityMeter ? electricityMeter.imei : '',
      electricityMeterConnectionType: getMeterConnectionType(electricityMeter),
      gasMeterImei: gasMeter ? gasMeter.imei : '',
      gasMeterConnectionType: getMeterConnectionType(gasMeter)
    });
    
    setOpen(true);
  };
  
  const handleClose = () => {
    setOpen(false);
    setModalMode('add');
    setEditPropertyId(null);
    setNewProperty(initialPropertyState);
    setVisibleMeters({
      water: false,
      electricity: false,
      gas: false
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = event.target;

    if (name === 'unitNumber') {
      setNewProperty(prev => ({...prev, unitNumber: sanitizeUnitNumber(value)}));
      return;
    }

    setNewProperty(prev => ({...prev, [name]: value}));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedUnitNumber = sanitizeUnitNumber(newProperty.unitNumber);

    const formattedProperty: any = {
      development: newProperty.development,
      unitNumber: normalizedUnitNumber,
      type: newProperty.type,
      phase: newProperty.phase,
      ownerIds: newProperty.ownerIds,
    };

    const buildMeterPayload = (
      imei: string,
      service: 'water' | 'electricity' | 'gas',
      connectionType: MeterConnectionType,
    ) => {
      const meterPayload = { imei, service };

      if (connectionType === 'token') {
        return {
          ...meterPayload,
          ...TOKEN_METER_DEFAULTS,
        };
      }

      return meterPayload;
    };

    // Add meters if IMEIs are provided
    const meters = [];
    
    if (newProperty.waterMeterImei) {
      meters.push(buildMeterPayload(newProperty.waterMeterImei, 'water', newProperty.waterMeterConnectionType));
    }
    
    if (newProperty.electricityMeterImei) {
      meters.push(buildMeterPayload(
        newProperty.electricityMeterImei,
        'electricity',
        newProperty.electricityMeterConnectionType,
      ));
    }
    
    if (newProperty.gasMeterImei) {
      meters.push(buildMeterPayload(newProperty.gasMeterImei, 'gas', newProperty.gasMeterConnectionType));
    }
    
    if (meters.length > 0) {
      formattedProperty.meters = meters;
    }

    // If editing, add the property ID
    if (modalMode === 'edit' && editPropertyId) {
      formattedProperty.id = editPropertyId;
    }
    

    
    try {
      const response = modalMode === 'edit'
        ? await patchProperty({propertyPartial: formattedProperty}).unwrap()
        : await postProperty({ property: formattedProperty }).unwrap();

      console.log(modalMode === 'edit' ? 'Property updated:' : 'Property added:', response);

      setNewProperty(initialPropertyState);
      setVisibleMeters({
        water: false,
        electricity: false,
        gas: false
      });
      handleClose();
    } catch (err) {
      console.error(modalMode === 'edit' ? 'Failed to update property:' : 'Failed to add property:', err);
    }
  };

  const handleOwnersChange = (event: React.SyntheticEvent, newValue: User[]) => {
    const validOwnerIds = newValue.map(user => user.id).filter(id => id !== undefined) as string[];
    setNewProperty(prev => ({
      ...prev,
      ownerIds: validOwnerIds
    }));
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const name = event.target.name as keyof typeof newProperty;
    const value = event.target.value as string; // Assuming all values are strings
    setNewProperty(prev => ({...prev, [name]: value}));
  };
  
  const toggleMeter = (meterType: 'water' | 'electricity' | 'gas') => {
    setVisibleMeters(prev => ({
      ...prev,
      [meterType]: !prev[meterType]
    }));
  };

  if (isLoading) return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center', 
      height: '50vh',
      gap: 2
    }}>
      <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main' }} />
      <Typography variant="h6" color="primary" sx={{ mt: 2, fontWeight: 'medium' }}>
        Loading Properties...
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        We're preparing your property data. This will only take a moment.
      </Typography>
    </Box>
  );
  
  if (isError) return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      p: 3, 
      bgcolor: 'error.light', 
      borderRadius: 2,
      mt: 2
    }}>
      <Typography variant="h6" color="error.dark">
        Error loading properties
      </Typography>
      <Typography variant="body2" color="error.dark" sx={{ mt: 1 }}>
        Please try refreshing the page or contact support if the problem persists.
      </Typography>
    </Box>
  );

  const developmentOptions: { label: string, value: string }[] = [
    {label: 'Millennium Heights', value: 'millennium'},
    {label: 'Pokugara Residential Estate', value: 'pokugara'},
    {label: 'Pomona City', value: 'pomona'}
  ];

  const millenniumPhases: { label: string, value: string }[] = [
    {label: 'Block 1', value: 'block-1'},
    {label: 'Block 2', value: 'block-2'},
    {label: 'Block 3', value: 'block-3'},
    {label: 'Block 4', value: 'block-4'},
  ];

  const pokugaraPhases: { label: string, value: string }[] = [
    {label: 'PRET1', value: 'pret-1'},
    {label: 'PRET2', value: 'pret-2'},
    {label: 'PRET Stands', value: 'pret-stands'},
  ];

  const pomonaPhases: { label: string, value: string }[] = [
    {label: 'Phase 1A', value: '1A'},
    {label: 'Phase 1B', value: '1B'},
    {label: 'Phase 1C', value: '1C'},
  ];

  const apartmentSizes: { label: string, value: string }[] = [
    {label: 'Studio', value: 'studio'},
    {label: '1 Bedroom', value: '1-bed'},
    {label: '2 Bedroom', value: '2-bed'},
    {label: '3 Bedroom', value: '3-bed'},
  ];

  const pokugaraTypes: { label: string, value: string }[] = [
    {label: 'Stand', value: 'stand'},
    {label: 'Townhouse', value: 'townhouse'},
    {label: 'Garden', value: 'garden'},
    {label: 'Manor', value: 'manor'},
    {label: 'Villa', value: 'villa'},
  ];

  const pomonaTypes: { label: string, value: string }[] = [
    {label: 'Stand', value: 'stand'}
  ]

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 } }}>
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Properties Management
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          View, search, and manage all properties across different developments.
        </Typography>
      </Box>
      
      {/* Search and Filter Controls */}
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 3, md: 4 } }}>
        <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search Properties"
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="Search by development, type, or unit number"
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Filter by Owner Email"
              variant="outlined"
              value={filterOwnerEmail}
              onChange={handleOwnerEmailFilterChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="edwin@gmail.com"
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="development-filter-label">Development</InputLabel>
              <Select
                labelId="development-filter-label"
                value={filterDevelopment}
                onChange={handleDevelopmentFilterChange}
                label="Development"
              >
                <MenuItem value="">
                  <em>All Developments</em>
                </MenuItem>
                {developmentOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="type-filter-label">Property Type</InputLabel>
              <Select
                labelId="type-filter-label"
                value={filterType}
                onChange={handleTypeFilterChange}
                label="Property Type"
              >
                <MenuItem value="">
                  <em>All Types</em>
                </MenuItem>
                {[...apartmentSizes, ...pokugaraTypes, ...pomonaTypes]
                  .filter((option, index, self) => 
                    self.findIndex(o => o.value === option.value) === index
                  )
                  .map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterNoEmail}
                  onChange={handleFilterNoEmailChange}
                  color="warning"
                />
              }
              label="No email only"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="contained" 
              color="primary" 
              fullWidth
              onClick={handleOpen}
              startIcon={<AddIcon />}
              sx={{ height: { sm: '40px' } }}
            >
              Add Property
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleExportFilteredProperties}
              sx={{ height: { sm: '40px' } }}
            >
              Export Excel
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Properties Table with Sorting */}
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ 
          display: { xs: 'none', md: 'block' } // Hide table on mobile, show cards instead
        }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'background.default' }}>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'development'}
                      direction={orderBy === 'development' ? order : 'asc'}
                      onClick={() => handleRequestSort('development')}
                    >
                      Development
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'unitNumber'}
                      direction={orderBy === 'unitNumber' ? order : 'asc'}
                      onClick={() => handleRequestSort('unitNumber')}
                    >
                      Unit Number
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'type'}
                      direction={orderBy === 'type' ? order : 'asc'}
                      onClick={() => handleRequestSort('type')}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Owner Emails</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProperties.map((prop) => (
                  <TableRow 
                    key={prop.id}
                    hover
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      } 
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip 
                          label={developmentInfo(prop.development).name} 
                          color={
                            prop.development === 'millennium' ? 'primary' : 
                            prop.development === 'pokugara' ? 'secondary' : 
                            'default'
                          }
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPropertyNumber(prop)}
                      </Typography>
                    </TableCell>
                    <TableCell>{prop.type && formatPropertyType(prop.type ?? '')}</TableCell>

                    <TableCell>
                      {prop.ownerEmails && prop.ownerEmails.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {prop.ownerEmails.map((email, index) => (
                            <Chip
                              key={index}
                              label={email}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No owners assigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit Property">
                        <IconButton 
                          color="primary" 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEdit(prop);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {prop.phase === 'block-4' && (
                        <Tooltip title="Delete Duplicate Property">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProperty(prop.id ?? '', formatPropertyNumber(prop));
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                
                {paginatedProperties.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        No properties found matching your criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        
        {/* Mobile Card View */}
        <Box sx={{ 
          display: { xs: 'block', md: 'none' },
          p: 2
        }}>
          {paginatedProperties.length > 0 ? (
            <Grid container spacing={2}>
              {paginatedProperties.map((prop) => (
                <Grid item xs={12} key={prop.id}>
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={developmentInfo(prop.development).name} 
                            color={
                              prop.development === 'millennium' ? 'primary' : 
                              prop.development === 'pokugara' ? 'secondary' : 
                              'default'
                            }
                            size="small"
                            sx={{ mb: 1 }}
                          />
                          <IconButton 
                            color="primary" 
                            size="small"
                            onClick={() => handleOpenEdit(prop)}
                            sx={{ p: 0.5 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {prop.phase === 'block-4' && (
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDeleteProperty(prop.id ?? '', formatPropertyNumber(prop))}
                              sx={{ p: 0.5 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                        <Typography variant="body2" fontWeight="medium">
                          {formatPropertyNumber(prop)}
                        </Typography>
                      </Box>
                      
                      <Grid container spacing={1} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Type
                          </Typography>
                          <Typography variant="body2">
                            {prop.type && formatPropertyType(prop.type ?? '')}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Owners
                          </Typography>
                          {prop.ownerIds && prop.ownerIds.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {prop.ownerIds.map((ownerId, index) => (
                                <Chip
                                  key={index}
                                  label={ownerId.substring(0, 8)}
                                  size="small" 
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No owners assigned
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No properties found matching your criteria
              </Typography>
            </Box>
          )}
        </Box>
        
        {/* Pagination */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: { xs: 2, sm: 3 },
          borderTop: '1px solid',
          borderColor: 'divider',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Showing {paginatedProperties.length} of {filteredAndSortedProperties.length} properties
          </Typography>
          
          <Pagination 
            count={Math.ceil(filteredAndSortedProperties.length / rowsPerPage)} 
            page={page} 
            onChange={handleChangePage}
            color="primary"
            showFirstButton
            showLastButton
            size="small"
            siblingCount={1}
            sx={{ 
              '& .MuiPaginationItem-root': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }
            }}
          />
          
          <FormControl variant="outlined" size="small" sx={{ minWidth: 80, display: { xs: 'none', sm: 'block' } }}>
            <Select
              value={rowsPerPage.toString()}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(1);
              }}
              displayEmpty
            >
              <MenuItem value="5">5</MenuItem>
              <MenuItem value="10">10</MenuItem>
              <MenuItem value="25">25</MenuItem>
              <MenuItem value="50">50</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="add-property-modal-title"
        aria-describedby="add-property-modal-description"
      >
        <Box sx={{
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          width: { xs: '90vw', sm: '80vw', md: '60vw', lg: '50vw' }, 
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper', 
          borderRadius: 2,
          boxShadow: 24
        }}>
          <Box sx={{ 
            p: 3, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: '8px 8px 0 0'
          }}>
            <Typography id="add-property-modal-title" variant="h5" component="h2" fontWeight="bold" sx={{color: 'white'}}>
              {modalMode === 'add' ? 'Add New Property' : 'Edit Property'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8, color: 'white' }}>
              {modalMode === 'add' 
                ? 'Fill in the details below to add a new property to the system'
                : 'Update the property details below'}
            </Typography>
          </Box>
          
          <form onSubmit={handleSubmit}>
            <Box sx={{ p: 4 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                    Property Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="development-label">Development</InputLabel>
                    <Select
                      labelId="development-label"
                      name="development"
                      label="Development"
                      value={newProperty.development}
                      onChange={handleSelectChange}
                    >
                      {developmentOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" disabled={!newProperty.development}>
                    <InputLabel id="phase-label">Phase</InputLabel>
                    <Select
                      labelId="phase-label"
                      name="phase"
                      label="Phase"
                      value={newProperty.phase}
                      onChange={handleSelectChange}
                    >
                      {newProperty.development === 'millennium' &&
                        millenniumPhases.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      {newProperty.development === 'pokugara' &&
                        pokugaraPhases.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      {newProperty.development === 'pomona' &&
                        pomonaPhases.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" disabled={!newProperty.phase}>
                    <InputLabel id="type-label">Type</InputLabel>
                    <Select
                      labelId="type-label"
                      name="type"
                      label="Type"
                      value={newProperty.type}
                      onChange={handleSelectChange}
                    >
                      {newProperty.development === 'millennium' &&
                        (newProperty.phase === 'block-1' || newProperty.phase === 'block-2' || newProperty.phase === 'block-3'
                          ? apartmentSizes
                            .filter((option) => option.value === 'studio' || option.value === '1-bed')
                            .map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))
                          : apartmentSizes.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          )))}

                      {newProperty.development === 'pokugara' &&
                        (newProperty.phase === 'pret-1'
                          ? pokugaraTypes
                            .filter((option) => option.value === 'townhouse')
                            .map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))
                          : newProperty.phase === 'pret-2'
                            ? pokugaraTypes
                              .filter((option) => ['manor', 'villa', 'garden'].includes(option.value))
                              .map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))
                            : newProperty.phase === 'pret-stands'
                              ? pokugaraTypes
                                .filter((option) => option.value === 'stand')
                                .map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))
                              : pokugaraTypes.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              )))}

                      {newProperty.development === 'pomona' &&
                        pomonaTypes.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Unit Number"
                    name="unitNumber"
                    value={newProperty.unitNumber}
                    onChange={handleChange}
                    helperText={'Enter only the numeric part of the unit number unless same number exists for same Phase and development e.g 98A & 98B'}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                      Meters
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Add utility meters for this property
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Chip
                        icon={<WaterIcon />}
                        label="Water Meter"
                        clickable
                        color={visibleMeters.water ? "primary" : "default"}
                        onClick={() => toggleMeter('water')}
                        variant={visibleMeters.water ? "filled" : "outlined"}
                      />
                      <Chip
                        icon={<BoltIcon />}
                        label="Electricity Meter"
                        clickable
                        color={visibleMeters.electricity ? "primary" : "default"}
                        onClick={() => toggleMeter('electricity')}
                        variant={visibleMeters.electricity ? "filled" : "outlined"}
                      />
                      <Chip
                        icon={<GasMeterIcon />}
                        label="Gas Meter"
                        clickable
                        color={visibleMeters.gas ? "primary" : "default"}
                        onClick={() => toggleMeter('gas')}
                        variant={visibleMeters.gas ? "filled" : "outlined"}
                      />
                    </Box>
                  </Box>
                </Grid>

                {visibleMeters.water && (
                  <Grid item xs={12} md={visibleMeters.electricity || visibleMeters.gas ? 6 : 12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Water Meter IMEI"
                        name="waterMeterImei"
                        value={newProperty.waterMeterImei}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <WaterIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <FormControl fullWidth>
                        <InputLabel id="water-meter-connection-label">Meter Type</InputLabel>
                        <Select
                          labelId="water-meter-connection-label"
                          label="Meter Type"
                          name="waterMeterConnectionType"
                          value={newProperty.waterMeterConnectionType}
                          onChange={handleSelectChange}
                        >
                          <MenuItem value="wifi">WiFi Meter</MenuItem>
                          <MenuItem value="token">Token Meter</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                )}

                {visibleMeters.electricity && (
                  <Grid item xs={12} md={visibleMeters.water || visibleMeters.gas ? 6 : 12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Electricity Meter IMEI"
                        name="electricityMeterImei"
                        value={newProperty.electricityMeterImei}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BoltIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <FormControl fullWidth>
                        <InputLabel id="electricity-meter-connection-label">Meter Type</InputLabel>
                        <Select
                          labelId="electricity-meter-connection-label"
                          label="Meter Type"
                          name="electricityMeterConnectionType"
                          value={newProperty.electricityMeterConnectionType}
                          onChange={handleSelectChange}
                        >
                          <MenuItem value="wifi">WiFi Meter</MenuItem>
                          <MenuItem value="token">Token Meter</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                )}

                {visibleMeters.gas && (
                  <Grid item xs={12} md={visibleMeters.water || visibleMeters.electricity ? 6 : 12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Gas Meter IMEI"
                        name="gasMeterImei"
                        value={newProperty.gasMeterImei}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <GasMeterIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <FormControl fullWidth>
                        <InputLabel id="gas-meter-connection-label">Meter Type</InputLabel>
                        <Select
                          labelId="gas-meter-connection-label"
                          label="Meter Type"
                          name="gasMeterConnectionType"
                          value={newProperty.gasMeterConnectionType}
                          onChange={handleSelectChange}
                        >
                          <MenuItem value="wifi">WiFi Meter</MenuItem>
                          <MenuItem value="token">Token Meter</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary', fontWeight: 'medium', mt: 2 }}>
                    Ownership
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={12}>
                  {!!users?.length ? (
                    <Autocomplete
                      multiple
                      options={users}
                      getOptionLabel={(option: User) => option.email}
                      onChange={handleOwnersChange}
                      value={users.filter(user => newProperty.ownerIds.includes(user.id ?? ""))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Owner Emails"
                          placeholder="Add owners"
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.email}
                            {...getTagProps({ index })}
                            color="primary"
                            variant="outlined"
                          />
                        ))
                      }
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No users available to assign as owners
                    </Typography>
                  )}
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary"
                  startIcon={modalMode === 'add' ? <AddIcon /> : <EditIcon />}
                >
                  {modalMode === 'add' ? 'Add Property' : 'Save Changes'}
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
      </Modal>
    </Container>
  );
};

export default Properties;
